import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AdminApi } from "../api/admin-api";
import { User, UserRole } from "../api/types";
import AdminLayout from "../components/AdminLayout";
import { toast } from "sonner";

const AVAILABLE_ROLES = [
  { value: "amplifiles-superuser", label: "Amplifiles Superuser" },
  { value: "test-role", label: "Test Role" },
] as const;

export default function AddRole() {
  const [email, setEmail] = useState("");
  const [user, setUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const assignRoleMutation = useMutation({
    mutationFn: async ({
      userId,
      role,
      userEmail,
    }: {
      userId: number;
      role: string;
      userEmail: string;
    }) => {
      return AdminApi.assignRole(userId, role, userEmail);
    },
    onSuccess: (data) => {
      setSuccess(`${data.message}`);
      toast.success("Role assigned successfully");

      // Refresh user roles
      if (user) {
        AdminApi.getUserRoles(user.id).then(setUserRoles).catch(console.error);
      }

      // Reset form
      setSelectedRole("");
    },
    onError: (error) => {
      console.error("Failed to assign role:", error);
      setError(`Failed to assign role: ${error.message}`);
      toast.error("Failed to assign role");
    },
  });

  const handleSearchUser = async () => {
    if (!email.trim()) {
      setError("Please enter an email address");
      return;
    }

    setIsSearching(true);
    setError(null);
    setSuccess(null);
    setUser(null);
    setUserRoles([]);
    setSelectedRole("");

    try {
      const userData = await AdminApi.getUserByEmail(email.trim());
      setUser(userData);

      // Load current roles for the user
      const roles = await AdminApi.getUserRoles(userData.id);
      setUserRoles(roles);
    } catch (err: any) {
      console.error("Search failed:", err);
      if (err.message?.includes("not found") || err.response?.status === 404) {
        setError(`No user found with email: ${email}`);
      } else {
        setError(`Failed to search for user: ${err.message}`);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleAssignRole = async () => {
    if (!user || !selectedRole) {
      setError("Please select a role to assign");
      return;
    }

    // Check if user already has this role
    const hasRole = userRoles.some((role) => role.role === selectedRole);
    if (hasRole) {
      setError(`User already has the ${selectedRole} role`);
      return;
    }

    setError(null);
    setSuccess(null);
    assignRoleMutation.mutate({
      userId: user.id,
      role: selectedRole,
      userEmail: user.contactEmail,
    });
  };

  const getRoleLabel = (roleValue: string) => {
    const role = AVAILABLE_ROLES.find((r) => r.value === roleValue);
    return role?.label || roleValue;
  };

  const isRoleAlreadyAssigned = (roleValue: string) => {
    return userRoles.some((role) => role.role === roleValue);
  };

  return (
    <AdminLayout
      title="Assign Role to User"
      description="Search for a user by email and assign them a superuser role"
      breadcrumb="Assign Roles"
    >
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Step 1: User Search */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-3">1. Search User</h2>
          <div className="flex gap-3">
            <input
              type="email"
              placeholder="Enter user email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSearching}
            />
            <button
              onClick={handleSearchUser}
              disabled={isSearching || !email.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* User Information */}
        {user && (
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">User Found</h3>
            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <span className="font-medium">Name:</span>{" "}
                {user.firstName || user.lastName
                  ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                  : "Not provided"}
              </div>
              <div>
                <span className="font-medium">Email:</span> {user.contactEmail}
              </div>
              <div>
                <span className="font-medium">User ID:</span> {user.id}
              </div>
              <div>
                <span className="font-medium">Created:</span>{" "}
                {new Date(user.createdAt).toLocaleDateString()}
              </div>
            </div>

            {/* Current Roles */}
            <div className="mt-4 pt-3 border-t border-green-200">
              <h4 className="font-medium text-green-800 mb-2">
                Current Roles:
              </h4>
              {userRoles.length === 0 ? (
                <div className="text-sm text-gray-600">No roles assigned</div>
              ) : (
                <div className="space-y-1">
                  {userRoles.map((role) => (
                    <div key={role.id} className="text-sm">
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {getRoleLabel(role.role)}
                      </span>
                      <span className="ml-2 text-gray-500">
                        Assigned {new Date(role.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Role Assignment */}
        {user && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">2. Assign Role</h2>
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <div className="font-medium">Selected User:</div>
                <div className="text-sm text-gray-600">
                  {user.firstName || user.lastName
                    ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                    : "No name"}{" "}
                  ({user.contactEmail})
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Role to Assign
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={assignRoleMutation.isPending}
                >
                  <option value="">-- Select a role --</option>
                  {AVAILABLE_ROLES.map((role) => (
                    <option
                      key={role.value}
                      value={role.value}
                      disabled={isRoleAlreadyAssigned(role.value)}
                    >
                      {role.label}
                      {isRoleAlreadyAssigned(role.value)
                        ? " (Already assigned)"
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleAssignRole}
                disabled={
                  assignRoleMutation.isPending ||
                  !selectedRole ||
                  isRoleAlreadyAssigned(selectedRole)
                }
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assignRoleMutation.isPending ? "Assigning..." : "Assign Role"}
              </button>
            </div>
          </div>
        )}

        {/* Error Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="text-red-800 font-medium">Error</div>
            <div className="text-red-600 text-sm mt-1">{error}</div>
          </div>
        )}

        {/* Success Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <div className="text-green-800 font-medium">Success</div>
            <div className="text-green-600 text-sm mt-1">{success}</div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
